import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { SITE } from "@/lib/site";
import {
  Terminal, Code2, Target, Award, Github, Linkedin,
  ChevronRight, Shield, Cpu, Globe, ExternalLink,
} from "lucide-react";

/* ─── style injection ─── */
function useStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-ctf-fonts]")) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Syne:wght@700;800&family=JetBrains+Mono:wght@300;400;500;700&display=swap";
      l.setAttribute("data-ctf-fonts", "1");
      document.head.appendChild(l);
    }
    if (!document.querySelector("style[data-ap]")) {
      const s = document.createElement("style");
      s.setAttribute("data-ap", "1");
      s.textContent = AP_CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ─── data ─── */
const SKILLS = [
  { category: "Web Exploitation",            icon: Globe,   color: "#00d4ff", items: ["SQL Injection","XSS","SSTI","SSRF","LFI/RFI","Auth Bypass","Deserialization"] },
  { category: "Active Directory",            icon: Shield,  color: "#00ff88", items: ["Kerberoasting","AS-REP Roasting","DCSync","Pass-the-Hash","Golden Ticket","BloodHound"] },
  { category: "Linux Privilege Escalation",  icon: Terminal,color: "#ff8c42", items: ["SUID/SGID","Capabilities","Kernel Exploits","Cron Jobs","PATH Hijacking","Container Escape"] },
  { category: "Windows Privilege Escalation",icon: Cpu,     color: "#cc44ff", items: ["Token Impersonation","Service Exploits","UAC Bypass","DLL Hijacking","PrintSpoofer","Potato Attacks"] },
  { category: "Tools & Frameworks",          icon: Code2,   color: "#ffbd2e", items: ["Nmap","Burp Suite","Metasploit","BloodHound","Impacket","Hashcat","Ghidra","GDB"] },
  { category: "Programming & Scripting",     icon: Terminal,color: "#00ff88", items: ["Python","Bash","PowerShell","SQL","JavaScript","C"] },
];

const CERTS = [
  {
    category: "Offensive Security",
    items: [
      { name:"OSCP+",    desc:"Offensive Security Certified Professional Plus", org:"Offensive Security" },
      { name:"CRTO",     desc:"Certified Red Team Operator",                    org:"ZeroPoint Security" },
      { name:"Pentest+", desc:"CompTIA Pentest+",                               org:"CompTIA"            },
      { name:"eWPTXv2",  desc:"Web Application Penetration Tester eXtreme",    org:"eLearnSecurity"     },
      { name:"eJPT",     desc:"Junior Penetration Tester",                      org:"eLearnSecurity"     },
      { name:"DCPT",     desc:"Desec Certified Penetration Tester",             org:"Desec Security"     },
    ],
  },
  {
    category: "Cloud & Governance",
    items: [
      { name:"AZ-900",    desc:"Microsoft Azure Fundamentals", org:"Microsoft" },
      { name:"ITIL v3",   desc:"ITIL v3 Foundation",           org:"AXELOS"    },
      { name:"COBIT 4.1", desc:"COBIT 4.1 Foundation",         org:"ISACA"     },
    ],
  },
];

const PHASES = [
  { n:"01", name:"Reconnaissance",    desc:"Information gathering"  },
  { n:"02", name:"Enumeration",       desc:"Service discovery"      },
  { n:"03", name:"Exploitation",      desc:"Initial access"         },
  { n:"04", name:"Post-Exploitation", desc:"Privilege escalation"   },
  { n:"05", name:"Reporting",         desc:"Documentation"          },
];

const FACTS = [
  { k:"Focus",    v:"Offensive Sec" },
  { k:"Learning", v:"HTB / PG"      },
  { k:"Location", v:"Remote"        },
];

const HIGHLIGHTS = [
  { k:"Core Discipline", v:"Penetration Testing" },
  { k:"Primary Track", v:"Red Team Operations" },
  { k:"Strength", v:"Hands-on exploitation" },
];

/* ─── helpers ─── */
const Blink = () => <span className="ap-blink" />;

const SectionHdr = ({ icon: Icon, label, color = "#00ff88" }) => (
  <div className="ap-sec-hdr">
    <span className="ap-sec-icon" style={{ color }}><Icon size={15} /></span>
    <span className="ap-sec-title">{label}</span>
    <span className="ap-sec-rule" />
  </div>
);

/* ════════════════════════════════════════════
   MAIN
════════════════════════════════════════════ */
const AboutPage = () => {
  useStyles();
  const [activeSkill, setActiveSkill] = useState(null);
  const certCount = CERTS.reduce((sum, group) => sum + group.items.length, 0);
  const skillDomainCount = SKILLS.length;

  return (
    <div className="ap-root" data-testid="about-page">
      <SEO
        title="About"
        description="Maxwell Ferreira — Offensive security professional specializing in penetration testing, red team operations, and CTF challenges."
      />

      {/* ── hero header ── */}
      <div className="ap-hero">
        <div className="ap-hero-grid"   aria-hidden="true" />
        <div className="ap-hero-glow"   aria-hidden="true" />
        <div className="ap-hero-inner">
          <div className="ap-breadcrumb">
            <span className="ap-prompt">$</span>
            cat /home/maxwell/about.md
            <Blink />
          </div>
          <h1 className="ap-title">About Me</h1>
          <p className="ap-tagline">
            Offensive security professional · CTF player · Red team operator
          </p>
          <div className="ap-hero-metrics">
            <div className="ap-hero-metric">
              <span className="ap-hero-metric-num">{certCount}</span>
              <span className="ap-hero-metric-lbl">Certifications</span>
            </div>
            <div className="ap-hero-metric">
              <span className="ap-hero-metric-num">{skillDomainCount}</span>
              <span className="ap-hero-metric-lbl">Skill Domains</span>
            </div>
            <div className="ap-hero-metric">
              <span className="ap-hero-metric-num">HTB / PG</span>
              <span className="ap-hero-metric-lbl">Practice Platforms</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── layout ── */}
      <div className="ap-layout">

        {/* ════ LEFT / MAIN ════ */}
        <main className="ap-main">

          {/* bio */}
          <section className="ap-section">
            <SectionHdr icon={Terminal} label="Who I Am" />
            <div className="ap-card ap-bio">
              <div className="ap-bio-bar" />
              <div className="ap-bio-body">
                <p>
                  I'm <span className="ap-hi">Maxwell Ferreira</span>, a cybersecurity
                  professional focused on offensive security and hands-on learning through
                  CTF platforms.
                </p>
                <p>
                  I regularly solve machines on Hack The Box and Proving Grounds, documenting
                  exploitation paths and lessons learned along the way.
                </p>
                <p>
                  This site reflects my continuous growth in security and my commitment to
                  sharing practical knowledge with the community.
                </p>
              </div>
            </div>
          </section>

          <section className="ap-section">
            <SectionHdr icon={Award} label="Professional Snapshot" color="#ffbd2e" />
            <div className="ap-snapshot-grid">
              {HIGHLIGHTS.map((item) => (
                <div key={item.k} className="ap-snapshot-card">
                  <div className="ap-snapshot-k">{item.k}</div>
                  <div className="ap-snapshot-v">{item.v}</div>
                </div>
              ))}
            </div>
          </section>

          {/* skills */}
          <section className="ap-section">
            <SectionHdr icon={Code2} label="Technical Skills" color="#00d4ff" />
            <div className="ap-skills-grid">
              {SKILLS.map((sg, i) => {
                const Icon = sg.icon;
                const isActive = activeSkill === i;
                return (
                  <div
                    key={sg.category}
                    className={`ap-skill-card${isActive ? " is-active" : ""}`}
                    style={{ "--sk-c": sg.color, "--sk-r": sg.color }}
                    onMouseEnter={() => setActiveSkill(i)}
                    onMouseLeave={() => setActiveSkill(null)}
                  >
                    <div className="ap-sk-hdr">
                      <span className="ap-sk-icon" style={{ color: sg.color }}>
                        <Icon size={14} />
                      </span>
                      <span className="ap-sk-cat">{sg.category}</span>
                    </div>
                    <div className="ap-sk-chips">
                      {sg.items.map(item => (
                        <span key={item} className="ap-chip">{item}</span>
                      ))}
                    </div>
                    <div className="ap-sk-bar" style={{ background: sg.color }} />
                  </div>
                );
              })}
            </div>
          </section>

          {/* methodology */}
          <section className="ap-section">
            <SectionHdr icon={Target} label="Methodology" color="#ff8c42" />
            <div className="ap-card">
              <p className="ap-meth-intro">
                My approach to penetration testing follows a structured methodology:
              </p>
              <div className="ap-phases">
                {PHASES.map((p, i) => (
                  <div key={p.n} className="ap-phase">
                    <div className="ap-phase-n">{p.n}</div>
                    {i < PHASES.length - 1 && (
                      <div className="ap-phase-connector" aria-hidden="true" />
                    )}
                    <div className="ap-phase-name">{p.name}</div>
                    <div className="ap-phase-desc">{p.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </main>

        {/* ════ RIGHT / SIDEBAR ════ */}
        <aside className="ap-sidebar">

          {/* profile card */}
          <div className="ap-profile-card">
            <div className="ap-profile-scan" aria-hidden="true" />
            {SITE.photoUrl ? (
              <img
                src={SITE.photoUrl}
                alt={SITE.name}
                className="ap-avatar ap-avatar-photo"
                width="96"
                height="96"
                loading="lazy"
              />
            ) : (
              <div className="ap-avatar">
                <Terminal size={32} color="#00ff88" />
              </div>
            )}
            <div className="ap-profile-name">{SITE.name}</div>
            <div className="ap-profile-role">Offensive Security</div>
            <div className="ap-profile-sub">Penetration Testing · Red Team</div>
            <div className="ap-social">
              {/* Driven by lib/site.js — a null URL hides the icon instead of
                  linking to the service's own homepage. */}
              {SITE.social.linkedin && (
                <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer"
                  className="ap-social-btn" aria-label="LinkedIn" data-testid="about-linkedin">
                  <Linkedin size={16} />
                </a>
              )}
              {SITE.social.hackTheBox && (
                <a href={SITE.social.hackTheBox} target="_blank" rel="noopener noreferrer"
                  className="ap-social-btn" aria-label="Hack The Box profile" data-testid="about-htb">
                  <Target size={16} />
                </a>
              )}
              {SITE.social.github && (
                <a href={SITE.social.github} target="_blank" rel="noopener noreferrer"
                  className="ap-social-btn" aria-label="GitHub" data-testid="about-github">
                  <Github size={16} />
                </a>
              )}
            </div>
          </div>

          {/* quick facts */}
          <div className="ap-panel">
            <div className="ap-panel-lbl">
              <span className="ap-panel-pre">// </span>Quick Facts
            </div>
            <ul className="ap-facts">
              {FACTS.map(f => (
                <li key={f.k} className="ap-fact">
                  <span className="ap-fact-k">{f.k}</span>
                  <span className="ap-fact-v">{f.v}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* certifications */}
          <div className="ap-panel">
            <div className="ap-panel-lbl">
              <Award size={12} color="#00ff88" />
              <span className="ap-panel-pre"> // </span>Certifications
            </div>
            {CERTS.map(group => (
              <div key={group.category} className="ap-cert-group">
                <div className="ap-cert-cat">{group.category}</div>
                <ul className="ap-cert-list">
                  {group.items.map(c => (
                    <li key={c.name} className="ap-cert-item">
                      <div className="ap-cert-left">
                        <div className="ap-cert-name">{c.name}</div>
                        <div className="ap-cert-org">{c.org}</div>
                      </div>
                      <span className="ap-cert-badge">Earned</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="ap-cta">
            <div className="ap-cta-scan" aria-hidden="true" />
            <div className="ap-cta-title">Let's Connect</div>
            <p className="ap-cta-desc">
              Interested in my work or have an opportunity? I'd love to hear from you.
            </p>
            <Link to="/contact" className="ap-cta-btn" data-testid="about-contact-btn">
              <ChevronRight size={13} /> Get In Touch
            </Link>
          </div>

        </aside>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   CSS
════════════════════════════════════════════ */
const AP_CSS = `
@keyframes ap-fadein   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
@keyframes ap-blink    { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes ap-scanline { 0%{transform:translateX(-100%)} 100%{transform:translateX(350%)} }
@keyframes ap-pulse    { 0%,100%{opacity:.5} 50%{opacity:1} }

.ap-root {
  min-height:100vh; background:#070a0e;
  background-image:
    linear-gradient(rgba(0,255,136,.012) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.012) 1px,transparent 1px);
  background-size:44px 44px;
  font-family:'JetBrains Mono',ui-monospace,monospace; color:#b8c4d0;
}
.ap-blink {
  display:inline-block; width:8px; height:14px; background:#00ff88;
  vertical-align:middle; margin-left:3px;
  animation:ap-blink 1.1s step-end infinite;
}

/* ── hero ── */
.ap-hero {
  position:relative; overflow:hidden; padding:5rem 0 3rem;
  border-bottom:1px solid #1a2738;
}
.ap-hero-grid {
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(0,255,136,.025) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.025) 1px,transparent 1px);
  background-size:44px 44px;
  -webkit-mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.9) 30%,rgba(0,0,0,.9) 70%,transparent);
  mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.9) 30%,rgba(0,0,0,.9) 70%,transparent);
}
.ap-hero-glow {
  position:absolute; inset:0; pointer-events:none;
  background:
    radial-gradient(ellipse 700px 350px at 50% -10%,rgba(0,255,136,.07) 0%,transparent 70%),
    radial-gradient(ellipse 400px 300px at 90% 60%,rgba(0,212,255,.04) 0%,transparent 60%);
}
.ap-hero-inner {
  position:relative; z-index:1;
  max-width:1160px; margin:0 auto; padding:0 2.5rem;
  animation:ap-fadein .5s ease both;
}
.ap-breadcrumb {
  display:inline-flex; align-items:center; gap:8px;
  font-family:'Share Tech Mono',monospace; font-size:12px;
  color:#56657a; margin-bottom:1.3rem;
  border:1px solid #1a2738; background:#0c1118;
  padding:5px 14px; border-radius:2px;
}
.ap-prompt { color:#00ff88 }
.ap-title {
  font-family:'Syne',system-ui,sans-serif;
  font-size:clamp(2rem,5vw,3.2rem); font-weight:800;
  color:#e2eaf3; letter-spacing:-.025em; margin:0 0 .5rem;
  line-height:1.05;
}
.ap-tagline { font-size:13px; color:#56657a; margin:0; letter-spacing:.02em }
.ap-hero-metrics {
  display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px;
  max-width:760px; margin-top:1.35rem;
}
.ap-hero-metric {
  padding:.95rem 1rem; background:#0c1118; border:1px solid #1a2738; border-radius:3px;
}
.ap-hero-metric-num {
  display:block; font-family:'Syne',system-ui,sans-serif;
  font-size:1.35rem; font-weight:800; line-height:1; color:#e2eaf3;
}
.ap-hero-metric-lbl {
  display:block; margin-top:5px; font-family:'Share Tech Mono',monospace;
  font-size:9px; letter-spacing:.14em; text-transform:uppercase; color:#56657a;
}

/* ── layout ── */
.ap-layout {
  display:grid; grid-template-columns:1fr 300px; gap:2rem; align-items:start;
  max-width:1160px; margin:0 auto; padding:2.5rem 2.5rem 6rem;
}
@media(max-width:960px) { .ap-layout{grid-template-columns:1fr} .ap-sidebar{display:contents} }

/* ── main ── */
.ap-main  { display:flex; flex-direction:column; gap:2.8rem }
.ap-section { animation:ap-fadein .4s ease both }

/* section header */
.ap-sec-hdr   { display:flex; align-items:center; gap:9px; margin-bottom:1.4rem }
.ap-sec-icon  { flex-shrink:0 }
.ap-sec-title { font-family:'Syne',system-ui,sans-serif; font-size:16px; font-weight:700; color:#e2eaf3; letter-spacing:-.01em }
.ap-sec-rule  { flex:1; height:1px; background:linear-gradient(90deg,#1a2738,transparent) }

/* card */
.ap-card {
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:1.8rem;
}

/* bio */
.ap-bio     { display:flex; gap:0; padding:0; overflow:hidden }
.ap-bio-bar { width:3px; flex-shrink:0; background:linear-gradient(180deg,#00ff88,#00d4ff) }
.ap-bio-body { padding:1.6rem 1.8rem; display:flex; flex-direction:column; gap:.85rem }
.ap-bio-body p { margin:0; font-size:13.5px; color:#8faabf; line-height:1.8 }
.ap-hi { color:#00ff88; font-weight:700 }

.ap-snapshot-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:1rem }
.ap-snapshot-card {
  padding:1.1rem 1.15rem; background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  box-shadow:0 14px 35px rgba(0,0,0,.12);
}
.ap-snapshot-k {
  font-family:'Share Tech Mono',monospace; font-size:10px; letter-spacing:.14em;
  text-transform:uppercase; color:#56657a; margin-bottom:.45rem;
}
.ap-snapshot-v {
  font-family:'Syne',system-ui,sans-serif; font-size:1.05rem; font-weight:700;
  letter-spacing:-.01em; color:#e2eaf3;
}

/* skills grid */
.ap-skills-grid {
  display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:1rem;
}
.ap-skill-card {
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:1.3rem; position:relative; overflow:hidden;
  transition:border-color .2s; cursor:default;
}
.ap-skill-card.is-active { border-color:#243448 }
.ap-sk-hdr  { display:flex; align-items:center; gap:7px; margin-bottom:.9rem }
.ap-sk-icon { flex-shrink:0 }
.ap-sk-cat  {
  font-family:'Share Tech Mono',monospace; font-size:11px;
  text-transform:uppercase; letter-spacing:.07em; color:#56657a;
}
.ap-skill-card.is-active .ap-sk-cat { color:#b8c4d0 }
.ap-sk-chips { display:flex; flex-wrap:wrap; gap:5px }
.ap-chip {
  font-family:'Share Tech Mono',monospace; font-size:10px;
  padding:3px 8px; border-radius:2px;
  background:#070a0e; border:1px solid #1a2738; color:#56657a;
  transition:all .18s;
}
.ap-skill-card.is-active .ap-chip {
  border-color:rgba(var(--sk-c),0) ; /* fallback if needed */
  color:#8faabf;
}
.ap-sk-bar {
  position:absolute; bottom:0; left:0; right:0; height:2px; opacity:.25;
  transition:opacity .2s;
}
.ap-skill-card.is-active .ap-sk-bar { opacity:.6 }

/* methodology */
.ap-meth-intro { font-size:12.5px; color:#56657a; margin:0 0 1.6rem }
.ap-phases {
  display:grid; grid-template-columns:repeat(5,1fr); gap:0; position:relative;
}
@media(max-width:640px) { .ap-phases{grid-template-columns:1fr} }
@media(max-width:720px) {
  .ap-hero-metrics { grid-template-columns:1fr }
  .ap-snapshot-grid { grid-template-columns:1fr }
}

.ap-phase { display:flex; flex-direction:column; align-items:center; text-align:center; position:relative }
.ap-phase-connector {
  position:absolute; top:1.2rem; left:50%; width:100%;
  height:1px; background:linear-gradient(90deg,#1a2738 40%,transparent);
  pointer-events:none;
}
.ap-phase-n {
  font-family:'Share Tech Mono',monospace; font-size:20px; font-weight:400;
  color:#00ff88; opacity:.6; line-height:1;
  margin-bottom:.5rem;
}
.ap-phase-name { font-size:11.5px; color:#e2eaf3; font-weight:500; margin-bottom:.25rem; letter-spacing:-.01em }
.ap-phase-desc { font-size:10px; color:#56657a; letter-spacing:.02em }

/* ── sidebar ── */
.ap-sidebar { position:sticky; top:72px; display:flex; flex-direction:column; gap:1.1rem }
@media(max-width:960px) { .ap-sidebar{ position:static; order:-1 } }

/* profile card */
.ap-profile-card {
  position:relative; overflow:hidden; text-align:center;
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:2rem 1.5rem 1.8rem;
}
.ap-profile-scan {
  position:absolute; top:0; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,#00ff88,transparent);
  animation:ap-scanline 5s linear infinite;
}
.ap-avatar {
  width:72px; height:72px; border-radius:3px;
  border:1px solid rgba(0,255,136,.3); margin:0 auto 1.1rem;
  display:flex; align-items:center; justify-content:center;
  background:rgba(0,255,136,.05);
  box-shadow:0 0 18px rgba(0,255,136,.1);
}
.ap-avatar-photo {
  width:96px; height:96px;
  display:block; object-fit:cover;
  background:#0c1118;
}
.ap-profile-name { font-family:'Syne',system-ui,sans-serif; font-size:18px; font-weight:700; color:#e2eaf3; margin-bottom:.25rem }
.ap-profile-role { font-family:'Share Tech Mono',monospace; font-size:11px; color:#00ff88; letter-spacing:.08em; opacity:.7 }
.ap-profile-sub  { font-size:11.5px; color:#56657a; margin-top:.4rem }
.ap-social { display:flex; justify-content:center; gap:8px; margin-top:1.3rem }
.ap-social-btn {
  width:36px; height:36px; border-radius:3px;
  border:1px solid #1a2738; display:flex; align-items:center; justify-content:center;
  color:#56657a; text-decoration:none; transition:all .18s;
}
.ap-social-btn:hover { color:#00ff88; border-color:rgba(0,255,136,.4); background:rgba(0,255,136,.06) }

/* generic panel */
.ap-panel {
  background:#0c1118; border:1px solid #1a2738; border-radius:3px; padding:1.4rem;
}
.ap-panel-lbl {
  display:flex; align-items:center;
  font-family:'Share Tech Mono',monospace; font-size:10px;
  text-transform:uppercase; letter-spacing:.1em; color:#56657a;
  margin-bottom:1rem;
}
.ap-panel-pre { color:#00ff88 }

/* facts */
.ap-facts { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.55rem }
.ap-fact  { display:flex; justify-content:space-between; align-items:center }
.ap-fact-k { font-size:12px; color:#56657a }
.ap-fact-v { font-family:'Share Tech Mono',monospace; font-size:11px; color:#b8c4d0; letter-spacing:.04em }

/* certs */
.ap-cert-group { margin-bottom:1.3rem }
.ap-cert-group:last-child { margin-bottom:0 }
.ap-cert-cat {
  font-family:'Share Tech Mono',monospace; font-size:10px; text-transform:uppercase;
  letter-spacing:.08em; color:#00ff88; margin-bottom:.7rem; opacity:.6;
}
.ap-cert-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.55rem }
.ap-cert-item { display:flex; justify-content:space-between; align-items:flex-start; gap:.5rem }
.ap-cert-left { flex:1; min-width:0 }
.ap-cert-name { font-size:12.5px; color:#e2eaf3; font-weight:500 }
.ap-cert-org  { font-size:10.5px; color:#56657a; margin-top:1px }
.ap-cert-badge {
  font-family:'Share Tech Mono',monospace; font-size:9px; letter-spacing:.06em;
  padding:2px 7px; border-radius:2px; flex-shrink:0;
  background:rgba(0,255,136,.08); color:#00ff88; border:1px solid rgba(0,255,136,.2);
}

/* CTA */
.ap-cta {
  position:relative; overflow:hidden;
  background:linear-gradient(135deg,#0c1118,#091209);
  border:1px solid rgba(0,255,136,.2); border-radius:3px;
  padding:1.6rem 1.4rem;
}
.ap-cta-scan {
  position:absolute; top:0; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,#00ff88,transparent);
  animation:ap-scanline 4s linear infinite;
}
.ap-cta-title { font-family:'Syne',system-ui,sans-serif; font-size:15px; font-weight:700; color:#e2eaf3; margin-bottom:.5rem }
.ap-cta-desc  { font-size:12px; color:#56657a; line-height:1.65; margin:0 0 1.1rem }
.ap-cta-btn {
  display:inline-flex; align-items:center; gap:6px; width:100%; justify-content:center;
  padding:9px 0; border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:12px; letter-spacing:.05em;
  background:rgba(0,255,136,.1); color:#00ff88;
  border:1px solid rgba(0,255,136,.4); text-decoration:none;
  transition:all .18s;
}
.ap-cta-btn:hover { background:rgba(0,255,136,.18); box-shadow:0 0 18px rgba(0,255,136,.12) }
`;

export default AboutPage;
